import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { questions as experienceAssessmentQuestions } from '../assessments/experience/experience.assessment';
import { ExperienceAssessmentQuestion } from '../assessments/experience/experience.types';
import { AuthenticatedPage } from '../components/AuthenticatedPage';
import Container from '../components/container/Container';
import { ExperienceAssessment } from '../components/Experience/ExperienceAssessment';
import { ExperienceHeader } from '../components/Experience/ExperienceHeader';
import { usePreEmploymentAssessmentContext } from '../hooks/use-pre-employment-assessment.hooks';
import { useSWOTContext } from '../hooks/use-swot-context.hooks';
import { dispatchGetExperienceAssessmentValues } from '../redux/slice/form-experience';
import {
  PreEmploymentAssessmentStatus,
  dispatchUpsertPreEmploymentAssessment,
} from '../redux/slice/pre-employment-assessment';
import { dispatchGetSWOTReportValues } from '../redux/slice/swot/swot.operations';
import { SWOTReport } from '../redux/slice/swot/swot.types';
import { useAppDispatch } from '../redux/store';
import { splitArrayIntoChunks } from '../utils/form.utils';

export const Experience: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const swot: SWOTReport | undefined = useSWOTContext();
  const { componentName } = useParams();
  const { status: preEmploymentAssessmentStatus, requiresCareerReadinessAssessment } =
    usePreEmploymentAssessmentContext();
  const experienceAssessmentChunks: Array<ExperienceAssessmentQuestion[]> = splitArrayIntoChunks(
    experienceAssessmentQuestions,
    4,
  );
  useEffect(() => {
    void dispatch(dispatchGetExperienceAssessmentValues());
    void dispatch(dispatchUpsertPreEmploymentAssessment());
    void dispatch(dispatchGetSWOTReportValues());
  }, []);

  const [step, setStep] = useState(1);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBackButton = () => {
    if (!requiresCareerReadinessAssessment && step == 1) {
      navigate(`/pre-employment/career-readiness/step1`);
    } else if (requiresCareerReadinessAssessment && step == 1) {
      navigate(`/pre-employment/career-readiness/step2`);
    } else {
      navigate(`/pre-employment/experience/step${step - 1}`);
    }
  };

  useEffect(() => {
    setStep(parseInt(componentName?.replace('step', '') || '1'));
    window.scrollTo(0, 0);
  }, [componentName]);

  useEffect(() => {
    if (preEmploymentAssessmentStatus === PreEmploymentAssessmentStatus.NOT_STARTED) {
      void dispatch(
        dispatchUpsertPreEmploymentAssessment({
          status: PreEmploymentAssessmentStatus.ACTIVE,
        }),
      );
    }
  }, [preEmploymentAssessmentStatus]);

  useEffect(() => {
    if (swot) {
      navigate('/swot');
    }
  }, [swot, navigate]);

  const renderFormStep = () => {
    switch (componentName) {
      case 'step1':
        return (
          <ExperienceAssessment
            step={step}
            questions={experienceAssessmentChunks[0]}
            startIndex={1}
            onBack={handleBackButton}
          />
        );
      case 'step2':
        return (
          <ExperienceAssessment
            step={step}
            questions={experienceAssessmentChunks[1]}
            startIndex={experienceAssessmentChunks[0].length + 1}
            onBack={handleBackButton}
          />
        );
      case 'step3':
        return (
          <ExperienceAssessment
            step={step}
            questions={experienceAssessmentChunks[2]}
            startIndex={
              experienceAssessmentChunks[0].length + experienceAssessmentChunks[1].length + 1
            }
            onBack={handleBackButton}
          />
        );
      case 'step4':
        return (
          <ExperienceAssessment
            step={step}
            questions={experienceAssessmentChunks[3]}
            startIndex={
              experienceAssessmentChunks[0].length +
              experienceAssessmentChunks[1].length +
              experienceAssessmentChunks[2].length +
              1
            }
            onBack={handleBackButton}
            completePreEmploymentOnSubmission={true}
          />
        );
      default:
        navigate('/pre-employment/experience/step1');
        return null;
    }
  };

  return (
    <div className={`steps overflow-hidden relative experience-${step}`}>
      <Container>
        <>
          <ExperienceHeader>
            <>{renderFormStep()}</>
          </ExperienceHeader>
        </>
      </Container>
    </div>
  );
};

export const ExperiencePage: React.FC = () => {
  return <AuthenticatedPage render={() => <Experience />} />;
};
